import { type FC, useState, useEffect, JSX, useRef } from "react";
import api from "../services/api";
import { useData } from "../context/DataContext";
import { getStatusBadge, getCategoryBadge } from "../utils/utils";
import type { DashboardResponse, QrCode, Page, CategoryType, StatusType } from "../types";
import QrCodeDisplayModal from "../components/QrCodeDisplayModal";
import QrCodeListDisplayModal from "../components/QrCodeListDisplayModal";
import { useAuth } from "../context/AuthContext";
import CleanupModal from "../components/CleanUpModal";
import { useNavigate } from "react-router-dom";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import QRCode from "qrcode";

const StatCard: FC<{ title: string; value: string | number; icon: JSX.Element; colorClass: string }> = ({ title, value, icon, colorClass }) => (
    <div className="relative overflow-hidden bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-4 md:p-6 flex items-center space-x-4 md:space-x-5 transition-all duration-300 hover:shadow-lg hover:bg-slate-800/60 hover:-translate-y-1 group">
        <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity transform scale-150`}>
            {/* Decorative big icon in background */}
            <div className={colorClass}>{icon}</div>
        </div>

        <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shadow-lg ${colorClass} bg-opacity-20 flex-shrink-0`}>
            {/* Actual icon container with dynamic color background */}
            <div className="brightness-125">
                {icon}
            </div>
        </div>
        <div className="z-10">
            <p className="text-slate-400 text-xs md:text-sm font-medium tracking-wide uppercase">{title}</p>
            <p className="text-2xl md:text-3xl font-bold text-white mt-1">{value}</p>
        </div>
    </div>
);

const DashboardPage: FC = () => {
    // --- State Management ---
    const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
    const [qrCodePage, setQrCodePage] = useState<Page<QrCode> | null>(null);

    const [activeCategory, setActiveCategory] = useState<number>(-1);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [activeMenu, setActiveMenu] = useState<QrCode | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<QrCode | null>(null);
    const [showQrModal, setShowQrModal] = useState<QrCode | null>(null);

    // MODIFICA: isRefreshed ora indica che la tabella si sta aggiornando (loading state locale)
    const [isRefreshed, setIsRefreshed] = useState(false);

    const menuRef = useRef<HTMLDivElement>(null);
    const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // --- Export State ---
    const [showExportModal, setShowExportModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFilters, setExportFilters] = useState({
        status: 'ALL', // 'ALL' o 'ACTIVE'
        clientId: '', // stringa vuota = Tutti i clienti
        categoryIds: [] as number[], // array vuoto = Tutte le categorie
        includeCategory: true, // decidono se mostrare o no
        includeQrCode: false // <-- NUOVA PROPRIETÀ
    });

    // --- Selection State ---
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedQrCodes, setSelectedQrCodes] = useState<Map<number, QrCode>>(new Map());
    const [isFetchingAllForSelection, setIsFetchingAllForSelection] = useState(false);
    const [showSelectionPreview, setShowSelectionPreview] = useState(false);
    const [showTemplateFromSelection, setShowTemplateFromSelection] = useState(false);
    const [selectionError, setSelectionError] = useState<string | null>(null);

    const { categories, clients } = useData();
    const { user } = useAuth()
    const navigate = useNavigate();

    // --- Logic ---

    const fetchData = async (page: number, categoryId: number, search: string, onlyActive: boolean) => {
        // MODIFICA: Attiviamo l'effetto blur/loading all'inizio della chiamata
        setIsRefreshed(true);
        if (!qrCodePage) setIsLoading(true); // Loading globale solo al primo avvio

        setError(null);
        try {
            const cacheBuster = Date.now();
            if(user !== null && user.role === "COLLABORATOR"){
                const [qrCodesRes] = await Promise.all([
                    api.get('/api/qrcodes/getAll', {
                        params: {page, size: 20, idCategory: categoryId, search: search, onlyActive: onlyActive, _: cacheBuster},
                    }),
                ]);
                if (qrCodesRes.data.status) {
                    setQrCodePage(qrCodesRes.data.data);
                } else throw new Error("Impossibile caricare l'elenco dei QR code.");

            } else {
                const [dashboardRes, qrCodesRes] = await Promise.all([
                    api.get('/api/qrcodes/dashboard', {params: {_: cacheBuster}}),
                    api.get('/api/qrcodes/getAll', {
                        params: {page, size: 20, idCategory: categoryId, search: search, onlyActive: onlyActive, _: cacheBuster},
                    }),
                ]);

                if (dashboardRes.data.status) setDashboardData(dashboardRes.data.data);
                else throw new Error("Impossibile caricare i dati della dashboard.");

                if (qrCodesRes.data.status) {
                    setQrCodePage(qrCodesRes.data.data);
                } else throw new Error("Impossibile caricare l'elenco dei QR code.");
            }
        } catch (err: any) {
            console.error(err);
            // Non mostriamo l'errore globale se fallisce solo il refresh, per evitare di smontare la UI
            if(!qrCodePage) setError(err.message || "Si è verificato un errore di rete.");
        } finally {
            setIsLoading(false);
            // MODIFICA: Disattiviamo l'effetto blur alla fine
            setIsRefreshed(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // MODIFICA: Aggiunto 'user' alle dipendenze per ricaricare i dati se cambia il ruolo/login
    useEffect(() => {
        fetchData(currentPage, activeCategory, debouncedSearchTerm, false);
    }, [currentPage, activeCategory, debouncedSearchTerm, user]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const handleToggleMenu = (event: React.MouseEvent, qr: QrCode) => {
        event.stopPropagation();
        if (activeMenu?.id === qr.id) {
            setActiveMenu(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const menuHeight = 200;
        const menuWidth = 224;

        let top = rect.bottom + 8;
        if (top + menuHeight > window.innerHeight) {
            top = Math.max(10, rect.top - menuHeight - 8);
        }

        let left = rect.right - menuWidth;
        if (left < 10) {
            left = 10;
        }

        setMenuPosition({ top, left });
        setActiveMenu(qr);
    };

    const handleView = () => {
        if (!activeMenu) return;
        setShowQrModal(activeMenu);
        setActiveMenu(null);
    };

    const handleToggleActive = async () => {
        if (!activeMenu) return;
        try {
            await api.get(`/api/qrcodes/toggle-active/${activeMenu.id}`);
            fetchData(currentPage, activeCategory, debouncedSearchTerm, false);
        } catch (err) {
            setError("Errore durante l'aggiornamento dello stato.");
        }
        setActiveMenu(null);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    const handleDelete = () => {
        if (!activeMenu) return;
        setShowDeleteConfirm(activeMenu);
        setActiveMenu(null);
    };

    const confirmDelete = async () => {
        if (!showDeleteConfirm) return;
        try {
            await api.delete(`/api/qrcodes/delete/${showDeleteConfirm.id}`);
            setShowDeleteConfirm(null);
            if (qrCodePage && qrCodePage.content.length === 1 && currentPage > 0) {
                setCurrentPage(currentPage - 1);
            } else {
                fetchData(currentPage, activeCategory, debouncedSearchTerm, false);
            }
        } catch (err) {
            setError("Errore durante l'eliminazione.");
        }
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentPage(0);
        setActiveCategory(Number(e.target.value));
    };

    const resetModal = () => setIsCleanupModalOpen(false);
    const cleanUpSuccess = () => {
        setSearchTerm('');
        fetchData(0, -1, '', false);
    }

    // --- Selection Logic ---
    const toggleSelectionMode = () => {
        setIsSelectionMode(prev => {
            if (prev) {
                setSelectedQrCodes(new Map());
            }
            return !prev;
        });
        setSelectionError(null);
    };

    const toggleSelectQr = (qr: QrCode) => {
        setSelectedQrCodes(prev => {
            const next = new Map(prev);
            if (next.has(qr.id)) next.delete(qr.id);
            else next.set(qr.id, qr);
            return next;
        });
    };

    const isQrSelected = (id: number) => selectedQrCodes.has(id);

    const selectAllOnPage = () => {
        if (!qrCodePage) return;
        setSelectedQrCodes(prev => {
            const next = new Map(prev);
            qrCodePage.content.forEach(qr => next.set(qr.id, qr));
            return next;
        });
    };

    const deselectAllOnPage = () => {
        if (!qrCodePage) return;
        setSelectedQrCodes(prev => {
            const next = new Map(prev);
            qrCodePage.content.forEach(qr => next.delete(qr.id));
            return next;
        });
    };

    const allOnPageSelected = !!qrCodePage && qrCodePage.content.length > 0 &&
        qrCodePage.content.every(qr => selectedQrCodes.has(qr.id));

    const selectAllFiltered = async () => {
        setSelectionError(null);
        setIsFetchingAllForSelection(true);
        try {
            const res = await api.get('/api/qrcodes/getAll', {
                params: {
                    page: 0,
                    size: 100000,
                    idCategory: activeCategory,
                    search: debouncedSearchTerm,
                    onlyActive: false,
                    _: Date.now(),
                },
            });
            if (!res.data.status) throw new Error("Impossibile recuperare l'elenco completo.");
            const all: QrCode[] = res.data.data?.content || [];
            setSelectedQrCodes(prev => {
                const next = new Map(prev);
                all.forEach(qr => next.set(qr.id, qr));
                return next;
            });
        } catch (err: any) {
            setSelectionError(err.message || "Errore durante la selezione di tutti i risultati.");
        } finally {
            setIsFetchingAllForSelection(false);
        }
    };

    const clearSelection = () => setSelectedQrCodes(new Map());

    const removeFromSelection = (id: number) => {
        setSelectedQrCodes(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    };

    const selectedQrList = Array.from(selectedQrCodes.values());

    // --- Export Logic ---
    const handleCategoryToggleForExport = (id: number) => {
        setExportFilters(prev => {
            const isSelected = prev.categoryIds.includes(id);
            if (isSelected) {
                return { ...prev, categoryIds: prev.categoryIds.filter(cid => cid !== id) };
            } else {
                return { ...prev, categoryIds: [...prev.categoryIds, id] };
            }
        });
    };

    const handleExportData = async () => {
        setIsExporting(true);
        try {
            // Estrae tutti i QR dal DB
            const res = await api.get('/api/qrcodes/getAll', {
                params: {
                    page: 0,
                    size: 10000,
                    idCategory: -1,
                    search: '',
                    onlyActive: exportFilters.status === 'ACTIVE'
                }
            });

            let dataToExport: QrCode[] = res.data.data?.content || [];

            // Filtra per Cliente in locale (se selezionato)
            if (exportFilters.clientId !== '') {
                const selectedClient = clients.find(c => c.id.toString() === exportFilters.clientId);
                if (selectedClient) {
                    dataToExport = dataToExport.filter(qr => qr.client === selectedClient.name);
                }
            }

            // Filtra per Categorie in locale
            if (exportFilters.categoryIds.length > 0) {
                const selectedCategoryNames = categories
                    .filter(c => exportFilters.categoryIds.includes(c.id))
                    .map(c => c.name);
                dataToExport = dataToExport.filter(qr => selectedCategoryNames.includes(qr.category));
            }

            // --- INIZIO CREAZIONE EXCEL ---
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('QR Codes');

            // 1. Definisci le colonne
            const columns = [
                { header: 'ID', key: 'id', width: 10 },
                { header: 'Nome', key: 'name', width: 30 },
            ];

            if (exportFilters.includeCategory) {
                columns.push({ header: 'Categoria', key: 'category', width: 25 });
            }
            if (exportFilters.includeQrCode) {
                columns.push({ header: 'Immagine QR', key: 'qrcode', width: 20 });
            }

            worksheet.columns = columns;

            // Stile per l'intestazione
            worksheet.getRow(1).font = { bold: true };
            worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

            // 2. Aggiungi i dati e le immagini
            // USA UN CICLO FOR CLASSICO PER SUPPORTARE L'AWAIT
            for (let index = 0; index < dataToExport.length; index++) {
                const qr = dataToExport[index];

                const rowData: any = {
                    id: qr.id,
                    name: qr.name,
                };
                if (exportFilters.includeCategory) {
                    rowData.category = qr.category;
                }

                const row = worksheet.addRow(rowData);
                row.alignment = { vertical: 'middle' };

                // Gestione dell'immagine QR
                if (exportFilters.includeQrCode && qr.code) {
                    row.height = 100; // Allarga la riga per farci stare l'immagine

                    try {
                        // GENERA L'IMMAGINE IN BASE64 A PARTIRE DAL TESTO
                        const qrDataUrl = await QRCode.toDataURL(qr.code);

                        // Rimuovi il prefisso "data:image/png;base64,"
                        const base64Data = qrDataUrl.replace(/^data:image\/\w+;base64,/, "");

                        const imageId = workbook.addImage({
                            base64: base64Data,
                            extension: 'png',
                        });

                        // Aggiungi l'immagine alla cella (la riga è index + 2 perché la riga 1 è l'header)
                        worksheet.addImage(imageId, {
                            tl: { col: columns.length - 1, row: index + 1 }, // Posizione (Top-Left)
                            ext: { width: 120, height: 120 } // Dimensioni dell'immagine in px
                        });
                    } catch (qrError) {
                        console.error(`Errore nella generazione del QR per l'id ${qr.id}:`, qrError);
                    }
                }
            }

            // 3. Genera e scarica il file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Export_QrCode_${new Date().toISOString().split('T')[0]}.xlsx`);

            setShowExportModal(false);
        } catch (error) {
            console.error("Errore durante l'esportazione:", error);
            setError("Si è verificato un errore durante l'esportazione in Excel.");
        } finally {
            setIsExporting(false);
        }
    };

    if (isLoading && !qrCodePage) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-red-900/20 border border-red-800/50 backdrop-blur text-red-200 rounded-2xl flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>{error}</span>
            </div>
        );
    }

    // --- Icons (SVG) ---
    const Icons = {
        Total: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="6" height="6" x="3" y="3" rx="1"/><rect width="6" height="6" x="15" y="3" rx="1"/><rect width="6" height="6" x="3" y="15" rx="1"/><path d="M15 15h6v6h-6z"/></svg>,
        Active: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
        Expired: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
        Scan: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 12v5h12V8H9v4Z"/><path d="M18 8V5"/><path d="M14 8V5"/></svg>,
        Menu: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>,
        Search: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>,
        Excel: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h2"/><path d="M8 17h2"/><path d="M14 13h2"/><path d="M14 17h2"/></svg>,
        Select: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 11 3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
        Eye: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>,
        Template: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>,
        X: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
    };

    return (
        <div className="space-y-6 md:space-y-8 animate-fade-in">
            {/* Header Title */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">Dashboard</h1>
                    <p className="text-slate-400 mt-1 md:mt-2 text-base md:text-lg">Panoramica e gestione dei QR Code</p>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {user !== null && user.role !== "COLLABORATOR" && dashboardData && (
                    <>
                        <StatCard
                            title="QR Code Totali"
                            value={dashboardData.total}
                            colorClass="text-sky-400 bg-sky-500/10"
                            icon={Icons.Total}
                        />
                        <StatCard
                            title="Attivi"
                            value={dashboardData.active}
                            colorClass="text-emerald-400 bg-emerald-500/10"
                            icon={Icons.Active}
                        />
                        <StatCard
                            title="Scaduti"
                            value={dashboardData.expired}
                            colorClass="text-amber-400 bg-amber-500/10"
                            icon={Icons.Expired}
                        />
                        <StatCard
                            title="Scansioni (Oggi)"
                            value={`${dashboardData.dailyScanned} / ${dashboardData.dailyInvalid + dashboardData.dailyScanned}`}
                            colorClass="text-indigo-400 bg-indigo-500/10"
                            icon={Icons.Scan}
                        />
                    </>
                )}
            </div>

            {/* Main Content Card */}
            <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-xl overflow-hidden">

                {/* Toolbar */}
                <div className="p-4 md:p-6 border-b border-slate-700/50 flex flex-col md:flex-row justify-between items-center gap-4 bg-slate-800/20">
                    <div className="flex items-center gap-3 self-start md:self-auto w-full md:w-auto flex-wrap">
                        <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                            Elenco QR Code
                        </h2>
                        {/* Pulsante Esporta Excel */}
                        <button
                            onClick={() => setShowExportModal(true)}
                            className="ml-auto md:ml-2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg transition-colors shadow-sm"
                            title="Esporta in Excel (CSV)"
                        >
                            {Icons.Excel}
                            <span className="hidden sm:inline">Esporta Excel</span>
                        </button>
                        {/* Toggle Modalità Selezione */}
                        <button
                            onClick={toggleSelectionMode}
                            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors shadow-sm border ${isSelectionMode
                                ? 'text-sky-300 bg-sky-500/20 border-sky-500/40'
                                : 'text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 border-sky-500/30'}`}
                            title="Attiva selezione multipla"
                        >
                            {Icons.Select}
                            <span className="hidden sm:inline">{isSelectionMode ? 'Esci dalla selezione' : 'Modalità Selezione'}</span>
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <div className="relative group w-full sm:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-sky-400 transition-colors">
                                {Icons.Search}
                            </div>
                            <input
                                type="text"
                                placeholder="Cerca..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent block pl-10 p-2.5 transition-all hover:bg-slate-900/80"
                            />
                        </div>

                        <div className="relative w-full sm:w-48">
                            <select
                                onChange={handleCategoryChange}
                                value={activeCategory}
                                className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent block p-2.5 transition-all hover:bg-slate-900/80 appearance-none"
                            >
                                <option value={-1}>Tutte le categorie</option>
                                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-slate-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selection sub-toolbar */}
                {isSelectionMode && (
                    <div className="px-4 md:px-6 py-3 border-b border-slate-700/50 bg-sky-500/5 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="text-sm text-slate-300 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-sky-500/20 text-sky-300 font-semibold border border-sky-500/30">
                                {selectedQrCodes.size} selezionati
                            </span>
                            <span className="text-slate-500">·</span>
                            <span className="text-slate-400">Clicca su una riga per selezionarla</span>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:ml-auto">
                            <button
                                onClick={allOnPageSelected ? deselectAllOnPage : selectAllOnPage}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-200 bg-slate-700/60 hover:bg-slate-700 border border-slate-600/50 transition-colors"
                            >
                                {allOnPageSelected ? 'Deseleziona pagina' : 'Seleziona pagina'}
                            </button>
                            <button
                                onClick={selectAllFiltered}
                                disabled={isFetchingAllForSelection}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-sky-100 bg-sky-600/80 hover:bg-sky-600 border border-sky-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isFetchingAllForSelection && (
                                    <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                )}
                                Seleziona tutti i risultati del filtro
                            </button>
                            <button
                                onClick={clearSelection}
                                disabled={selectedQrCodes.size === 0}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Pulisci selezione
                            </button>
                        </div>
                    </div>
                )}
                {selectionError && (
                    <div className="px-4 md:px-6 py-2 bg-red-900/20 border-b border-red-800/40 text-red-200 text-sm">{selectionError}</div>
                )}

                {/* Table - Responsive Card Layout */}
                <div className="min-h-[400px] p-4 md:p-0">
                    <table className="w-full text-left block md:table border-collapse">
                        {/* Header: Nascosto su mobile, visibile su desktop */}
                        <thead className="hidden md:table-header-group bg-slate-900/60 text-xs uppercase text-slate-400 font-semibold tracking-wider">
                        <tr>
                            {isSelectionMode && (
                                <th className="p-5 border-b border-slate-700/50 w-12">
                                    <input
                                        type="checkbox"
                                        checked={allOnPageSelected}
                                        onChange={() => allOnPageSelected ? deselectAllOnPage() : selectAllOnPage()}
                                        className="rounded text-sky-500 bg-slate-800 border-slate-600 cursor-pointer focus:ring-sky-500"
                                        title={allOnPageSelected ? 'Deseleziona pagina' : 'Seleziona pagina'}
                                    />
                                </th>
                            )}
                            <th className="p-5 border-b border-slate-700/50">Nome / Descrizione</th>
                            <th className="p-5 border-b border-slate-700/50">Categoria</th>
                            <th className="p-5 border-b border-slate-700/50">Cliente</th>
                            <th className="p-5 border-b border-slate-700/50">Scadenza</th>
                            <th className="p-5 border-b border-slate-700/50">Stato</th>
                            <th className="p-5 border-b border-slate-700/50 text-right">Azioni</th>
                        </tr>
                        </thead>

                        {/* MODIFICA: Utilizziamo isRefreshed per gestire visivamente il caricamento (opacità ridotta) */}
                        <tbody className={`block md:table-row-group transition-all duration-300 ${isRefreshed ? 'opacity-50 blur-[1px] pointer-events-none' : 'opacity-100'}`}>
                        {qrCodePage && qrCodePage.content.map(qr => {
                            const selected = isQrSelected(qr.id);
                            return (
                            <tr
                                key={qr.id}
                                onClick={() => isSelectionMode ? toggleSelectQr(qr) : setShowQrModal(qr)}
                                className={`block md:table-row bg-slate-800/40 md:bg-transparent border md:border-none rounded-2xl md:rounded-none mb-4 md:mb-0 transition-colors group cursor-pointer shadow-lg md:shadow-none ${selected ? 'border-sky-500/60 md:bg-sky-500/5 hover:bg-sky-500/10' : 'border-slate-700/50 hover:bg-slate-700/30'}`}
                                title={isSelectionMode ? (selected ? 'Clicca per deselezionare' : 'Clicca per selezionare') : 'Clicca per visualizzare il QR Code'}
                            >
                                {isSelectionMode && (
                                    <td className="hidden md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0 w-12">
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={(e) => { e.stopPropagation(); toggleSelectQr(qr); }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="rounded text-sky-500 bg-slate-800 border-slate-600 cursor-pointer focus:ring-sky-500"
                                        />
                                    </td>
                                )}
                                {/* Cella: Nome */}
                                <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                    <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Nome</span>
                                    <div className="font-semibold text-white text-right md:text-left truncate max-w-[150px] sm:max-w-[250px] md:max-w-none flex items-center gap-2 md:justify-start justify-end">
                                        {isSelectionMode && (
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={(e) => { e.stopPropagation(); toggleSelectQr(qr); }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="md:hidden rounded text-sky-500 bg-slate-800 border-slate-600 cursor-pointer focus:ring-sky-500"
                                            />
                                        )}
                                        <span>{qr.name}</span>
                                    </div>
                                </td>

                                {/* Cella: Categoria */}
                                <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                    <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Categoria</span>
                                    <div className="text-right md:text-left">{getCategoryBadge(qr.category as CategoryType)}</div>
                                </td>

                                {/* Cella: Cliente */}
                                <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                    <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</span>
                                    <div className="text-slate-300 text-sm text-right md:text-left">{qr.client || <span className="text-slate-600 italic">N/A</span>}</div>
                                </td>

                                {/* Cella: Scadenza */}
                                <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                    <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Scadenza</span>
                                    <div className="text-slate-300 text-sm font-mono text-right md:text-left">{qr.validTo || <span className="text-slate-500">∞</span>}</div>
                                </td>

                                {/* Cella: Stato */}
                                <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                    <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Stato</span>
                                    <div className="text-right md:text-left">{getStatusBadge(qr.status as StatusType)}</div>
                                </td>

                                {/* Cella: Azioni */}
                                <td className="flex justify-between items-center md:table-cell p-4 md:p-5 text-right bg-slate-900/20 md:bg-transparent rounded-b-2xl md:rounded-none">
                                    <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Azioni</span>
                                    <button
                                        onClick={(e) => handleToggleMenu(e, qr)}
                                        className={`text-slate-400 hover:text-white hover:bg-slate-600 p-2 rounded-lg transition-all ${activeMenu?.id === qr.id ? 'bg-slate-700 text-white shadow-inner' : ''}`}
                                        title="Apri menu azioni"
                                    >
                                        {Icons.Menu}
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                        {qrCodePage?.content.length === 0 && (
                            <tr className="block md:table-row">
                                <td colSpan={isSelectionMode ? 7 : 6} className="block md:table-cell p-12 text-center text-slate-500 cursor-default hover:bg-transparent border-none">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <svg className="w-12 h-12 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        <p>Nessun QR Code trovato.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {qrCodePage && qrCodePage.totalPages > 1 && (
                    <div className="p-4 border-t border-slate-700/50 bg-slate-800/30 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-400">
                        <span>Pagina <span className="text-white font-semibold">{qrCodePage.number + 1}</span> di {qrCodePage.totalPages}</span>
                        <div className="flex space-x-2 w-full sm:w-auto">
                            <button
                                onClick={() => setCurrentPage(p => p - 1)}
                                disabled={qrCodePage.number === 0 || isRefreshed}
                                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs uppercase tracking-wide text-center"
                            >
                                Precedente
                            </button>
                            <button
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={qrCodePage.number >= qrCodePage.totalPages - 1 || isRefreshed}
                                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs uppercase tracking-wide text-center"
                            >
                                Successiva
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Admin Cleanup Section */}
            {user?.role === 'ADMIN' && (
                <div className="mt-8 md:mt-12 p-1 rounded-2xl bg-gradient-to-r from-red-900/50 to-rose-900/50 border border-rose-800/30">
                    <div className="bg-slate-900/90 backdrop-blur rounded-xl p-5 md:p-8 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-rose-100 mb-2 flex items-center justify-center md:justify-start gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                                Operazioni di Fine Ciclo
                            </h2>
                            <p className="text-rose-200/60 max-w-xl text-sm md:text-base">
                                Questa area permette di archiviare e pulire i dati dei QR code e delle scansioni.
                                <span className="block mt-1 text-xs uppercase font-bold tracking-wider">Attenzione: Le modifiche influenzano i dati live.</span>
                            </p>
                        </div>
                        <button
                            onClick={() => setIsCleanupModalOpen(true)}
                            className="relative group w-full md:w-auto px-6 py-3 rounded-xl font-bold bg-rose-600 text-white transition-all flex items-center justify-center space-x-2 flex-shrink-0"
                        >
                            {/* Effetto alone dietro al bottone (appare all'hover) */}
                            <div className="absolute inset-0 rounded-xl bg-rose-500 blur opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                            {/* Contenuto del bottone */}
                            <span className="relative z-10 flex items-center gap-2">Avvia Pulizia</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Context Menu */}
            {activeMenu && (
                <div
                    ref={menuRef}
                    style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
                    className="fixed w-56 rounded-xl bg-slate-800 border border-slate-700 shadow-2xl ring-1 ring-black ring-opacity-50 focus:outline-none z-50 overflow-hidden transform transition-all animate-in fade-in zoom-in-95 duration-100"
                >
                    <div className="py-1 divide-y divide-slate-700/50">
                        <div onClick={(e) => { e.stopPropagation(); handleView(); }} className="group flex items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white cursor-pointer transition-colors">
                            <span className="p-1.5 bg-slate-700/50 rounded-md mr-3 group-hover:bg-slate-600 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                            </span>
                            <span>Visualizza</span>
                        </div>

                        {user && (user.role === "ADMIN" || user.role === "ROLE_ADMIN") &&
                            <div onClick={(e) => { e.stopPropagation(); navigate("/admin/update/" + activeMenu?.id); }} className="group flex items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white cursor-pointer transition-colors">
                                <span className="p-1.5 bg-slate-700/50 rounded-md mr-3 group-hover:bg-slate-600 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                                </span>
                                <span>Modifica</span>
                            </div>
                        }

                        <div onClick={(e) => { e.stopPropagation(); handleToggleActive(); }} className="group flex items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white cursor-pointer transition-colors">
                            <span className={`p-1.5 rounded-md mr-3 group-hover:bg-slate-600 transition-colors ${activeMenu.status === 'Attivo' ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>
                            </span>
                            <span>{activeMenu.status === 'Attivo' ? 'Disattiva' : 'Attiva'}</span>
                        </div>

                        {user && (user.role === "ADMIN" || user.role === "ROLE_ADMIN") &&
                            <div onClick={(e) => { e.stopPropagation(); handleDelete(); }} className="group flex items-center px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 cursor-pointer transition-colors">
                                <span className="p-1.5 bg-red-500/10 rounded-md mr-3 group-hover:bg-red-500/20 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                </span>
                                <span>Elimina</span>
                            </div>
                        }
                    </div>
                </div>
            )}

            {/* Modals */}
            {showQrModal && <QrCodeDisplayModal qrCode={showQrModal} onClose={() => setShowQrModal(null)} />}

            {/* Modale Esporta Excel */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl transform transition-all scale-100 flex flex-col max-h-[90vh]">
                        {/* Header Modale */}
                        <div className="p-6 border-b border-slate-700/50 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                                    {Icons.Excel}
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Esporta in Excel</h3>
                                    <p className="text-xs text-slate-400">Scarica i tuoi QR Code in formato CSV</p>
                                </div>
                            </div>
                            <button onClick={() => setShowExportModal(false)} className="text-slate-400 hover:text-white transition-colors">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Corpo Modale - Scrollabile */}
                        <div className="p-6 space-y-6 overflow-y-auto">
                            {/* Filtro: Stato */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-3">Quali QR Code esportare?</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input type="radio" name="status" checked={exportFilters.status === 'ALL'} onChange={() => setExportFilters(p => ({ ...p, status: 'ALL' }))} className="text-emerald-500 focus:ring-emerald-500 bg-slate-900 border-slate-600 cursor-pointer" />
                                        <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Tutti</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input type="radio" name="status" checked={exportFilters.status === 'ACTIVE'} onChange={() => setExportFilters(p => ({ ...p, status: 'ACTIVE' }))} className="text-emerald-500 focus:ring-emerald-500 bg-slate-900 border-slate-600 cursor-pointer" />
                                        <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Solo Attivi</span>
                                    </label>
                                </div>
                            </div>

                            {/* Filtro: Cliente */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-2">Seleziona Cliente</label>
                                <select
                                    value={exportFilters.clientId}
                                    onChange={(e) => setExportFilters(p => ({ ...p, clientId: e.target.value }))}
                                    className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent block p-3 transition-all appearance-none"
                                >
                                    <option value="">Tutti i Clienti</option>
                                    {clients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtro: Categorie */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-2">Categorie (Nessuna selezionata = Tutte)</label>
                                <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-3 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {categories.map(cat => (
                                        <label key={cat.id} className="flex items-center gap-2 cursor-pointer group p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={exportFilters.categoryIds.includes(cat.id)}
                                                onChange={() => handleCategoryToggleForExport(cat.id)}
                                                className="rounded text-emerald-500 focus:ring-emerald-500 bg-slate-800 border-slate-600 cursor-pointer"
                                            />
                                            <span className="text-sm text-slate-300 truncate group-hover:text-white transition-colors">{cat.name}</span>
                                        </label>
                                    ))}
                                    {categories.length === 0 && <span className="text-slate-500 text-sm italic">Nessuna categoria trovata</span>}
                                </div>
                            </div>

                            {/* Colonne da Esportare */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-3">Colonne da includere</label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-slate-400">
                                        <input type="checkbox" checked disabled className="rounded text-emerald-500 bg-slate-800 border-slate-600 opacity-50" />
                                        <span className="text-sm">ID (Obbligatorio)</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-slate-400">
                                        <input type="checkbox" checked disabled className="rounded text-emerald-500 bg-slate-800 border-slate-600 opacity-50" />
                                        <span className="text-sm">Nome (Obbligatorio)</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={exportFilters.includeCategory}
                                            onChange={(e) => setExportFilters(p => ({ ...p, includeCategory: e.target.checked }))}
                                            className="rounded text-emerald-500 focus:ring-emerald-500 bg-slate-900 border-slate-600 cursor-pointer"
                                        />
                                        <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Includi Categoria</span>
                                    </label>

                                    {/* NUOVA CHECKBOX PER IL QR CODE */}
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={exportFilters.includeQrCode}
                                            onChange={(e) => setExportFilters(p => ({ ...p, includeQrCode: e.target.checked }))}
                                            className="rounded text-emerald-500 focus:ring-emerald-500 bg-slate-900 border-slate-600 cursor-pointer"
                                        />
                                        <span className="text-sm text-slate-300 group-hover:text-white transition-colors">Includi Immagine QR Code</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Footer Modale */}
                        <div className="p-4 bg-slate-900/50 border-t border-slate-700/50 rounded-b-2xl flex justify-end space-x-3 shrink-0">
                            <button
                                onClick={() => setShowExportModal(false)}
                                disabled={isExporting}
                                className="px-5 py-2.5 rounded-xl text-slate-300 font-medium hover:bg-slate-700 transition-colors disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleExportData}
                                disabled={isExporting}
                                className="px-5 py-2.5 rounded-xl text-white font-semibold bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isExporting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        Elaborazione...
                                    </>
                                ) : (
                                    <>Scarica File</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl transform transition-all scale-100">
                        <div className="p-6">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-500">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                            </div>
                            <h3 className="text-xl font-bold text-white">Conferma Eliminazione</h3>
                            <p className="text-slate-400 mt-2">
                                Sei sicuro di voler eliminare il QR code <span className="text-white font-semibold">"{showDeleteConfirm.name}"</span>?
                                <br/>Questa azione è irreversibile.
                            </p>
                        </div>
                        <div className="p-4 bg-slate-900/50 rounded-b-2xl flex justify-end space-x-3">
                            <button onClick={() => setShowDeleteConfirm(null)} className="px-5 py-2.5 rounded-xl text-slate-300 font-medium hover:bg-slate-700 transition-colors">Annulla</button>
                            <button onClick={confirmDelete} className="px-5 py-2.5 rounded-xl text-white font-semibold bg-red-600 hover:bg-red-500 shadow-lg shadow-red-500/20 transition-all">Elimina</button>
                        </div>
                    </div>
                </div>
            )}

            <CleanupModal
                isOpen={isCleanupModalOpen}
                onClose={() => resetModal()}
                onSuccess={cleanUpSuccess}
                clients={clients}
                categories={categories}
            />

            {/* Floating selection action bar */}
            {isSelectionMode && selectedQrCodes.size > 0 && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl">
                    <div className="bg-slate-900/95 backdrop-blur border border-slate-700 shadow-2xl rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-3">
                            <span className="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-300 flex items-center justify-center border border-sky-500/40">
                                {Icons.Select}
                            </span>
                            <div className="text-white">
                                <p className="text-sm font-semibold">{selectedQrCodes.size} QR Code selezionati</p>
                                <p className="text-xs text-slate-400">Pronti per anteprima o generazione</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:ml-auto">
                            <button
                                onClick={() => setShowSelectionPreview(true)}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-slate-200 bg-slate-700 hover:bg-slate-600 border border-slate-600 transition-colors"
                            >
                                {Icons.Eye} Anteprima
                            </button>
                            <button
                                onClick={() => setShowTemplateFromSelection(true)}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-500/20 transition-colors"
                            >
                                {Icons.Template} Genera con Modello
                            </button>
                            <button
                                onClick={clearSelection}
                                title="Pulisci selezione"
                                className="p-2 rounded-lg text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 transition-colors"
                            >
                                {Icons.X}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Selection preview modal */}
            {showSelectionPreview && selectedQrCodes.size > 0 && (
                <QrCodeListDisplayModal
                    qrCodes={selectedQrList}
                    onClose={() => setShowSelectionPreview(false)}
                    download={false}
                    onRemove={removeFromSelection}
                    title="Anteprima Selezione"
                    subtitle={`${selectedQrCodes.size} QR Code selezionati`}
                />
            )}

            {/* Template generation modal from selection */}
            {showTemplateFromSelection && selectedQrCodes.size > 0 && (
                <QrCodeListDisplayModal
                    qrCodes={selectedQrList}
                    onClose={() => setShowTemplateFromSelection(false)}
                    download={true}
                    onRemove={removeFromSelection}
                    title="Genera con Modello"
                    subtitle={`${selectedQrCodes.size} QR Code da elaborare`}
                />
            )}
        </div>
    );
};

export default DashboardPage;
